/* eslint-disable @typescript-eslint/no-shadow */
import { AudioElement } from '../types'
import { DEFAULT_CURRENT_TIME } from 'd2/hooks/useAudioPlayer'
import {
  memo,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import type { Duration } from 'd2/hooks/useAudioPlayer/types'
import type { Props } from './types'

const STYLE = { display: 'none' }

const Audio = memo<Props>(({
  autoPlay,
  autoPlayOnTrackChange,
  currentTime,
  duration,
  loop,
  muted,
  onDurationChange,
  onPause,
  onPlay,
  playing,
  preload,
  setCurrentTime,
  setDuration,
  setPlaying,
  src,
  volume,
}) => {
  const audioElementRef = useRef<AudioElement | null>(null)

  const handleDurationChange = useCallback(() => {
    if (audioElementRef.current) {
      const {
        duration,
      }: {
        duration: Duration
      } = audioElementRef.current
      setDuration(duration)
      if (typeof onDurationChange === 'function') {
        onDurationChange(duration)
      }
    }
  }, [onDurationChange, setDuration])

  const handleRef = useCallback((ref: AudioElement | null) => {
    audioElementRef.current = ref
  }, [])
  const handleTimeUpdate = useCallback(() => {
    if (audioElementRef.current) {
      setCurrentTime(audioElementRef.current.currentTime)
    }
  }, [setCurrentTime])
  const handleTrackChange = useCallback(() => {
    const audio = audioElementRef.current
    if (audio) {
      audio.pause()
      setCurrentTime(DEFAULT_CURRENT_TIME)
      audio.load()
      if (autoPlayOnTrackChange) {
        audio.play().catch((error) => {
          // eslint-disable-next-line no-console
          console.warn(error)
        })
      }
    }
  }, [autoPlayOnTrackChange, setCurrentTime])
  const handleTrackFinish = useCallback(() => {
    if (currentTime === duration) {
      setPlaying(false)
    }
  }, [currentTime, duration, setPlaying])
  const setAudioCurrentTime = useCallback(() => {
    if (audioElementRef.current && Math.abs(audioElementRef.current.currentTime - currentTime) >= 0.1) {
      audioElementRef.current.currentTime = currentTime
    }
  }, [currentTime])
  const setAudioPlaying = useCallback(() => {
    (playing ? audioElementRef.current?.play() : audioElementRef.current?.pause())?.catch((error) => {
      // eslint-disable-next-line no-console
      console.warn(error)
    })
  }, [playing])
  const setAudioVolume = useCallback(() => {
    if (audioElementRef.current) {
      audioElementRef.current.volume = volume
    }
  }, [volume])

  useEffect(() => {
    setAudioPlaying()
  }, [playing, setAudioPlaying])
  useEffect(() => {
    setAudioVolume()
  }, [volume, setAudioVolume])
  useEffect(() => {
    setAudioCurrentTime()
    handleTrackFinish()
  }, [currentTime, handleTrackFinish, setAudioCurrentTime])
  useEffect(() => {
    handleTrackChange()
  }, [handleTrackChange, src])

  return (
    <audio
      autoPlay={autoPlay}
      controls={false}
      loop={loop}
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      muted={muted || volume === 0}
      onDurationChange={handleDurationChange}
      onPause={onPause}
      onPlay={onPlay}
      onTimeUpdate={handleTimeUpdate}
      preload={preload}
      ref={handleRef}
      style={STYLE}
    >
      <source src={src} />
    </audio>
  )
})

Audio.displayName = 'Audio'

export default Audio
